import { NavPage } from '@/components/page/Breadcrumbs';
import { PageContainer } from '@/components/page/PageContainer';
import { PageHeader } from '@/components/page/PageHeader';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { VerticalMenu } from '@/components/shared/menu/VerticalMenu';
import { gqlToSkills, useGetSkill } from '@/services/tasks/useSkills';
import { FC } from 'react';
import { FiStar } from 'react-icons/fi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import strings from '../../admin.translations';
import { deactivateReactivateTabConfig } from '../../common';
import AdminDeactivateReactivateSkillCard from './AdminDeactivateReactivateSkillCard';
import AdminEditSkillDetailsCard from './AdminEditSkillDetailsCard';

export const AdminEditSkillPage: FC = () => {
  const { tabName, skillId } = useParams();

  const { state } = useLocation();

  if (!skillId) throw new Error('Missing skill id');

  const navigate = useNavigate();

  const getSkill = useGetSkill();

  const skill = state?.skill ? gqlToSkills([state.skill])[0] : getSkill(skillId);

  if (!skill) {
    throw new Error(`Skill with ID ${skillId} not found`);
  }

  const breadcrumbs: NavPage[] = [
    {
      name: strings.page.admin,
      to: '/admin'
    },
    {
      name: strings.skillsSection.editSkill.skill,
      to: '/admin/skills'
    },
    {
      name: skill.name,
      to: `/admin/skills/${skillId}`
    }
  ];

  const { icon: DeactivateReactivateIcon, label: deactivateReactivateLabel } =
    deactivateReactivateTabConfig[skill.isActive ? 'active' : 'inactive'];

  const sideMenuOptions = [
    {
      icon: <FiStar className="h-5 w-5" />,
      label: strings.skillsSection.editSkill.skillDetails,
      anchorId: 'edit-location',
      element: <AdminEditSkillDetailsCard skill={skill} />
    },
    {
      icon: <DeactivateReactivateIcon className="h-5 w-5" />,
      label: deactivateReactivateLabel,
      anchorId: 'deactivate-reactivate',
      element: <AdminDeactivateReactivateSkillCard skill={skill} />
    }
  ];

  const allElements = (
    <>
      <AdminEditSkillDetailsCard skill={skill} />
      <AdminDeactivateReactivateSkillCard skill={skill} />
    </>
  );

  const selectedTab = tabName ?? sideMenuOptions[0].anchorId;
  const handleTabSelect = (anchorId: string) => {
    navigate(`/admin/skills/${skillId}/${anchorId}`);
  };

  return (
    <PageContainer
      customHeader={<PageHeader pageTitle={strings.page.title} icon={<FiStar />} breadcrumbs={breadcrumbs} />}
    >
      <PageSizer className="flex gap-5 flex-col lg:flex-row" mode={SizerMode.PAGE}>
        <div className="hidden lg:flex gap-5 w-full">
          <VerticalMenu
            onTabSelect={handleTabSelect}
            sideMenuOptions={sideMenuOptions}
            isScroll={false}
            selectedTab={selectedTab}
          />
        </div>
        <div className="lg:hidden">
          <VerticalMenu
            onTabSelect={handleTabSelect}
            sideMenuOptions={sideMenuOptions}
            allElements={allElements}
            isScroll={true}
            selectedTab={selectedTab}
          />
        </div>
      </PageSizer>
    </PageContainer>
  );
};
